import React, { useEffect, useRef } from "react";

import { XMarkIcon } from "@heroicons/react/20/solid";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import loadable from "@loadable/component";
import { Modal as AntModal } from "antd";
import classNames from "classnames";
import PropTypes from "prop-types";

import styles from "./Modal.module.less";

const Button = loadable(() => import("@/common/Button"));
const Spin = loadable(() => import("@/common/Spin"));

Modal.propTypes = {
  /** Whether the modal dialog is visible or not */
  visible: PropTypes.bool,
  /** Title to display in the modal. If there are no body text to display, you can pass this as `children`. */
  title: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  /** Icon to display beside copy (above for smaller screens). Presets include `error`, `warning`, `info`, and `success`. */
  icon: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.node,
    PropTypes.element,
  ]),
  /** Layout should only be compact, vertical, or custom */
  layout: PropTypes.oneOf(["compact", "vertical", "custom"]),
  /** Whether a close (x) button is visible on top right of the modal dialog or not */
  closable: PropTypes.bool,
  /** Whether to close the modal dialog when the mask (area outside the modal) is clicked */
  maskClosable: PropTypes.bool,
  /** Fixed header content, if any */
  fixedHeader: PropTypes.node,
  /** Footer content, set as `footer={null}` when you don't need default buttons */
  footer: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  /** Text of the OK button */
  okText: PropTypes.string,
  /** Text of the Cancel button */
  cancelText: PropTypes.string,
  /** Button type of the OK button */
  okType: PropTypes.string,
  /** Specify a function that will be called when the user clicks the OK button */
  onOk: PropTypes.func,
  okDisabled: PropTypes.bool,
  /** Specify a function that will be called when the user clicks the Cancel button, close button, or the mask */
  onCancel: PropTypes.func,
  /** Whether to show the Cancel button */
  hasCancelButton: PropTypes.bool,
  /** Size of the modal, either 'small', 'medium', or 'wide'. Defaults to 'small'. */
  size: PropTypes.string,
  /** Whether to show a spinner over the modal */
  loading: PropTypes.bool,
  /** Body text to display in the modal. If `title` is not specified, this will be displayed as the title instead. */
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  wrapperClassName: PropTypes.string,
  antModalClassName: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  /** Any other events and properties to be passed to the (antd) modal instance. */
  rest: PropTypes.any,
};

Modal.defaultProps = {
  visible: false,
  closable: true,
  maskClosable: true,
  layout: "compact",
  footer: "default",
  okText: "OK",
  cancelText: "Cancel",
  okType: "primary",
  okDisabled: false,
  hasCancelButton: true,
  size: "small",
  loading: false,
};

export default function Modal({
  visible,
  title,
  icon,
  closable,
  maskClosable,
  fixedHeader,
  footer,
  okText,
  cancelText,
  okType,
  onOk,
  onCancel,
  okDisabled,
  hasCancelButton,
  size,
  loading,
  children,
  wrapperClassName,
  antModalClassName,
  className,
  style,
  layout,
  ...rest
}) {
  let antModalKlass = classNames(styles.modal, antModalClassName);
  let modalKlass = classNames(
    `modal-${size} inline-block align-bottom bg-white rounded-2xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:align-middle sm:p-6`,
    className,
  );

  let titleBodyContainerKlass = classNames(
    "sm:w-full text-center h-full",
    layout === "vertical" ? "" : "sm:text-left",
    icon && layout === "compact" ? "mt-3 sm:mt-0 sm:ml-4" : null,
  );
  let bodyContainerKlass = classNames(title ? "h-full" : null);

  const scrollableBody = useRef();

  useEffect(() => {
    if (visible && scrollableBody?.current) {
      setTimeout(() => {
        // scroll body to top on show
        scrollableBody.current.scrollTop = 0;
      }, 0);
    }
  }, [visible]);

  function getIconDisplay() {
    switch (icon) {
      case React.isValidElement(icon):
        return icon;
      case "error":
        return (
          <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-red-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
          </div>
        );
      case "warning":
        return (
          <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-yellow-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
            <ExclamationCircleIcon className="w-6 h-6 text-yellow-600" />
          </div>
        );
      case "info":
        return (
          <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-blue-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
            <InformationCircleIcon className="w-6 h-6 text-blue-600" />
          </div>
        );
      case "success":
        return (
          <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-green-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
            <CheckCircleIcon className="w-6 h-6 text-green-600" />
          </div>
        );
    }
    return icon;
  }

  return (
    <AntModal
      open={visible}
      transitionName=""
      footer={null}
      closable={false}
      maskClosable={maskClosable}
      width="auto"
      onCancel={onCancel}
      wrapClassName={wrapperClassName}
      className={antModalKlass}
      centered={!!fixedHeader}
      {...rest}
    >
      <div
        className={modalKlass}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-headline"
        style={style}
      >
        <div className="absolute top-0 right-0 z-10 hidden sm:block">
          {closable && (
            <button
              type="button"
              className="p-4 text-gray-900 rounded-md hover:text-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              onClick={onCancel}
            >
              <span className="sr-only">Close</span>
              <XMarkIcon className="w-6 h-6 bg-transparent" />
            </button>
          )}
        </div>
        <Spin
          spinning={loading}
          wrapperClassName={classNames("flex-grow", fixedHeader && "h-[80vh]")}
        >
          {fixedHeader && <div className="mb-4">{fixedHeader}</div>}
          <div
            ref={scrollableBody}
            className={classNames(
              "flex-grow",
              layout === "compact" ? "sm:flex sm:items-start" : "",
              fixedHeader && "overflow-y-auto scrollbar -mx-6 px-6",
            )}
          >
            {icon && getIconDisplay()}
            {/* show `children` as title if no `title` is specified */}
            <div className={titleBodyContainerKlass}>
              <h3
                className={classNames(
                  "text-lg leading-6 font-medium text-gray-900",
                  title && closable ? "sm:pr-4" : "",
                  title ? "mb-2" : "mb-0",
                )}
                id="modal-headline"
              >
                {title || children}
              </h3>
              {title && (
                <div className={bodyContainerKlass}>
                  <div className="h-full text-sm text-gray-500">{children}</div>
                </div>
              )}
            </div>
          </div>

          {footer && (
            <div className={`${layout === "vertical" ? "w-full" : ""}`}>
              {/* render footer, if provided */}
              {footer !== "default" && footer}
              {/* else, render default footer */}
              {footer === "default" && (
                <div className="grid gap-3 mt-4 sm:flex sm:flex-row-reverse">
                  <Button
                    className={classNames(
                      "w-full cursor-pointer",
                      layout == "compact" ? "sm:w-auto" : "",
                    )}
                    disabled={okDisabled}
                    type={okType}
                    onClick={onOk}
                  >
                    {okText}
                  </Button>
                  {onCancel && hasCancelButton && (
                    <Button
                      className={classNames(
                        "w-full",
                        layout == "compact" ? "sm:w-auto" : "",
                      )}
                      onClick={onCancel}
                    >
                      {cancelText}
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </Spin>
      </div>
    </AntModal>
  );
}
